/**
 * MCP Servers Page — ArgusAI
 *
 * View MCP servers grouped by tier, circuit breaker status,
 * tool catalog per server, and agent-tool permission matrix.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  Plug, RefreshCw, Loader2, Shield, AlertTriangle,
  CheckCircle, XCircle, Lock, Unlock, RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/mcp')({
  component: McpPage,
});

interface McpServer {
  serverId: string;
  displayName: string;
  namespace: string;
  tier: string;
  toolCount: number;
  circuitBreaker: { state: string; failures: number; lastFailure: string | null };
  isHealthy: boolean;
}

interface McpTool {
  name: string;
  description: string;
  isWriteOperation: boolean;
  permissionLevel: string;
}

function McpPage() {
  const [servers, setServers] = useState<McpServer[]>([]);
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const [tools, setTools] = useState<McpTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{ data: McpServer[] }>('/mcp/servers');
      setServers(res.data);
    } catch (err) {
      console.error('Failed to load MCP servers:', err);
    }
    setLoading(false);
  };

  const selectServer = async (id: string) => {
    setSelectedServer(id);
    try {
      const res = await apiClient.get<{ tools: McpTool[] }>(`/mcp/servers/${id}/tools`);
      setTools(res.tools);
    } catch {
      setTools([]);
    }
  };

  const resetCircuitBreaker = async (id: string) => {
    try {
      await apiClient.post(`/mcp/servers/${id}/reset`, {});
      await loadServers();
    } catch {}
  };

  // Group servers by tier
  const tiers = servers.reduce((acc, s) => {
    if (!acc[s.tier]) acc[s.tier] = [];
    acc[s.tier].push(s);
    return acc;
  }, {} as Record<string, McpServer[]>);

  const cbColor = (state: string) => {
    switch (state) {
      case 'closed': return 'text-green-500';
      case 'half_open': return 'text-yellow-500';
      case 'open': return 'text-red-500';
      default: return 'text-muted-foreground';
    }
  };

  const cbBadge = (state: string) => {
    switch (state) {
      case 'closed': return 'default' as const;
      case 'half_open': return 'secondary' as const;
      case 'open': return 'destructive' as const;
      default: return 'outline' as const;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Deprecation banner */}
      {showBanner && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-700 dark:text-amber-400">
              This page has moved. <Link to="/connections" className="font-medium underline">Go to Connections</Link> for the full experience with API connections, health monitoring, and more.
            </span>
          </div>
          <button onClick={() => setShowBanner(false)} className="text-amber-600 hover:text-amber-800 text-sm" aria-label="Dismiss deprecation notice">
            Dismiss
          </button>
        </div>
      )}
    <div className="flex flex-1 overflow-hidden">
      {/* Left: Server list grouped by tier */}
      <div className="w-96 border-r flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Plug className="h-5 w-5" />
              MCP Servers
              <Badge variant="secondary">{servers.length}</Badge>
            </h2>
            <Button size="icon" variant="ghost" onClick={loadServers} aria-label="Refresh MCP servers" title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {servers.reduce((sum, s) => sum + s.toolCount, 0)} tools across {servers.length} servers
          </p>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-4">
            {Object.entries(tiers).map(([tier, tierServers]) => (
              <div key={tier}>
                <div className="px-3 py-1.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {tier}
                  </p>
                </div>
                <div className="space-y-1">
                  {tierServers.map(server => (
                    <button
                      key={server.serverId}
                      onClick={() => selectServer(server.serverId)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedServer === server.serverId
                          ? 'bg-primary/10 border border-primary/20'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{server.displayName}</span>
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${
                            server.isHealthy ? 'bg-green-500' : 'bg-red-500'
                          }`} />
                          <Badge variant="outline" className="text-xs">
                            {server.toolCount} tools
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span className={`font-mono ${cbColor(server.circuitBreaker.state)}`}>
                          {server.circuitBreaker.state}
                        </span>
                        {server.circuitBreaker.failures > 0 && (
                          <span className="text-red-400">
                            {server.circuitBreaker.failures} failures
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Right: Tool catalog for selected server */}
      <div className="flex-1 overflow-auto">
        {selectedServer ? (
          <div className="p-6 space-y-6">
            {(() => {
              const server = servers.find(s => s.serverId === selectedServer);
              if (!server) return null;

              return (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">{server.displayName}</h2>
                      <p className="text-muted-foreground">
                        {server.tier} · {server.namespace} namespace
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={cbBadge(server.circuitBreaker.state)} className="text-sm px-3 py-1">
                        Circuit: {server.circuitBreaker.state}
                      </Badge>
                      {server.circuitBreaker.state !== 'closed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resetCircuitBreaker(server.serverId)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Reset
                        </Button>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Health Card */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">Status</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          {server.isHealthy
                            ? <CheckCircle className="h-5 w-5 text-green-500" />
                            : <XCircle className="h-5 w-5 text-red-500" />}
                          <span className="font-medium">
                            {server.isHealthy ? 'Healthy' : 'Degraded'}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">Tools</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{server.toolCount}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">Failures</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{server.circuitBreaker.failures}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Tool Catalog */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Tool Catalog</CardTitle>
                      <CardDescription>
                        Available tools on this server
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {tools.map(tool => (
                          <div
                            key={tool.name}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50"
                          >
                            <div>
                              <p className="font-mono text-sm">{tool.name}</p>
                              <p className="text-xs text-muted-foreground">{tool.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {tool.isWriteOperation ? (
                                <Badge variant="destructive" className="text-xs">
                                  <Lock className="h-3 w-3 mr-1" />
                                  Write
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  <Unlock className="h-3 w-3 mr-1" />
                                  Read
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                        {tools.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            Loading tools...
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <Plug className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-lg font-medium">Select a server</p>
              <p className="text-sm">Click a server to view its tool catalog</p>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
